/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';

import {UserProfileApiService} from '../../app/admin/user-profile/user-profile-api.service';
import {LocalNetworkProfile} from '../session/local-network-profile';
import {LocalUserProfile} from '../session/local-user-profile';

const STARRED_KEY = 'adnStarred';

@Injectable({
  providedIn: 'root'
})
export class StarService {
  private user: any;
  private starred: any;

  constructor(
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
  ) {
    this.refresh();
  }

  getStarred(objectType?: any): any[] {
    if (!this.objectTypeSafe(objectType)) {
      return [];
    }
    this.refresh();
    const idArray = _.get(this.starred, [this.LocalNetworkProfile.get('networkId'), objectType.type]);
    return idArray ? idArray.slice() : [];
  }

  isStarred(objectId?: any, objectType?: any): boolean {
    const idArray = this.getStarred(objectType);
    return _.includes(idArray, objectId);
  }

  updateStar(objectId?: any, objectType?: any, makeStarred?: any): any {
    if (!objectId || !this.objectTypeSafe(objectType) || !_.isBoolean(makeStarred)) {
      return Promise.reject(new Error('A valid object, object type and starred state are required'));
    }
    const objKey = objectType.type;

    // need to refresh in case of another tab having starred something.
    this.refresh();
    const networkId = this.LocalNetworkProfile.get('networkId');
    if (!this.starred[networkId] || !this.starred[networkId][objKey]) {
      _.set(this.starred, [networkId, objKey], []);
    }
    if (_.includes(this.starred[networkId][objKey], objectId) === makeStarred) {
      return Promise.resolve();
    }
    if (makeStarred) {
      this.starred[networkId][objKey].push(objectId);
    } else {
      _.pull(this.starred[networkId][objKey], objectId);
    }
    return firstValueFrom(this.UserProfile.saveProfile({
      id: this.user.userId,
      profile: _.pick(this.user.profile, STARRED_KEY)
    })).then((apiUser: any) => {
      this.user = this.localUserProfile.safeSaveProfile(STARRED_KEY, apiUser.profile[STARRED_KEY]);
    });
  }

  private refresh(): void {
    this.user = this.localUserProfile.get();
    this.user.profile[STARRED_KEY] = this.user.profile[STARRED_KEY] || {};
    this.starred = this.user.profile[STARRED_KEY];
  }

  private objectTypeSafe(objectType: any): any {
    return objectType && objectType.type;
  }
}
